/*
 * Học 4F trước khi viết: giới thiệu (3 thẻ) → 4 bài ngắn → quiz → kết quả.
 * Ghi lượt xem intro/bài học một lần mỗi phiên (behaviorSessionId) như bản cũ.
 */
import { useEffect, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Icon, SemanticIcon } from "@/components/icon"
import { Meter, PageHeader, StatusNote } from "@/components/page"
import { behaviorSessionId, post } from "@/lib/api"
import { cn } from "@/lib/utils"
import { INTRO, score100 } from "./shared"

export type LearningPhase = "intro" | "learn" | "quiz" | "result"

function Dots({ total, at }: { total: number; at: number }) {
  return (
    <div className="flex gap-1.5" aria-label={`Bước ${at + 1} trên ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={cn("h-2 flex-1 rounded-full transition-colors", i <= at ? "bg-orange" : "bg-line")} />
      ))}
    </div>
  )
}

function Nav({ onBack, children }: { onBack?: () => void; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {onBack ? (
        <Button variant="ghost" onClick={onBack}>
          <Icon name="arrow-left" className="size-4" /> Trước
        </Button>
      ) : (
        <span />
      )}
      {children}
    </div>
  )
}

/** Thẻ trượt nhẹ khi đổi bước (chỉ dịch chuyển, không mờ). */
function StepCard({ k, children }: { k: string; children: React.ReactNode }) {
  const reduce = useReducedMotion()
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={k} initial={reduce ? false : { x: 16 }} animate={{ x: 0 }} transition={{ duration: 0.2 }}>
        <Card>
          <CardContent className="flex flex-col gap-6">{children}</CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  )
}

export function LearningFlow({
  slug,
  learning,
  onLearning,
  onWrite,
}: {
  slug: string
  learning: any
  onLearning: (data: any) => void
  onWrite: () => void
}) {
  const [phase, setPhase] = useState<LearningPhase>("intro")
  const [introIndex, setIntroIndex] = useState(0)
  const [learnIndex, setLearnIndex] = useState(0)
  const [quizIndex, setQuizIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [confidence, setConfidence] = useState(70)
  const [result, setResult] = useState<any>()
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState("")
  const lessons: any[] = learning?.lessons || []
  const questions: any[] = learning?.questions || []
  const path = `/api/classes/${encodeURIComponent(slug)}/reflection-learning`

  useEffect(() => {
    if (phase !== "intro" && phase !== "learn") return
    const kind = phase === "intro" ? "intro_view" : "lesson_view"
    const subject = phase === "intro" ? `intro_${introIndex + 1}` : String(lessons[learnIndex]?.part || "")
    if (!subject) return
    const key = `simba-four-f-view:${behaviorSessionId()}:${slug}:${kind}:${subject}`
    try {
      if (sessionStorage.getItem(key)) return
      sessionStorage.setItem(key, "1")
    } catch {
      return
    }
    post(path, { event_id: crypto.randomUUID(), kind, subject_code: subject }).catch(() => {
      try {
        sessionStorage.removeItem(key)
      } catch {}
    })
  }, [phase, introIndex, learnIndex, slug, lessons, path])

  const startQuiz = () => {
    setAnswers({})
    setQuizIndex(0)
    setResult(undefined)
    setMessage("")
    setPhase("quiz")
  }
  const submitQuiz = async () => {
    if (!questions.length || questions.some((q) => answers[q.id] == null)) return setMessage("Con hãy trả lời đủ các câu nhé.")
    setBusy(true)
    setMessage("")
    try {
      const res = await post(path, {
        event_id: crypto.randomUUID(),
        confidence: confidence / 100,
        answers: questions.map((q) => ({ question_id: q.id, option: answers[q.id] })),
      })
      setResult(res)
      onLearning(res)
      setPhase("result")
    } catch (e: any) {
      setMessage(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (phase === "intro") {
    const s = INTRO[introIndex]
    return (
      <>
        <PageHeader eyebrow={`Giới thiệu 4F · ${introIndex + 1}/${INTRO.length}`} title="4F giúp con điều gì?" />
        <StepCard k={`intro-${introIndex}`}>
          <Dots total={INTRO.length} at={introIndex} />
          <div className="flex flex-col items-start gap-3">
            <span className="grid size-14 place-items-center rounded-2xl bg-soft">
              <Icon name={s.icon} className="size-7 text-orange" />
            </span>
            <h2 className="font-heading text-2xl font-bold text-ink">{s.title}</h2>
            <p className="text-base text-muted-foreground">{s.copy}</p>
            <blockquote className="w-full rounded-2xl border-l-4 border-orange bg-sand px-4 py-3 text-base text-ink italic">{s.example}</blockquote>
          </div>
          <Nav onBack={introIndex > 0 ? () => setIntroIndex((i) => i - 1) : undefined}>
            {introIndex < INTRO.length - 1 ? (
              <Button onClick={() => setIntroIndex((i) => i + 1)}>
                Tiếp <Icon name="arrow-right" className="size-4" />
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setLearnIndex(0)
                  setPhase("learn")
                }}
              >
                Khám phá 4 chữ F <Icon name="arrow-right" className="size-4" />
              </Button>
            )}
          </Nav>
        </StepCard>
      </>
    )
  }

  if (phase === "learn" && lessons[learnIndex]) {
    const lesson = lessons[learnIndex]
    return (
      <>
        <PageHeader
          eyebrow={`Học 4F · ${learnIndex + 1}/${lessons.length}`}
          title={
            <span className="flex items-center gap-3">
              <span className="grid size-11 place-items-center rounded-2xl bg-soft">
                <SemanticIcon label={lesson.label} className="size-6 text-orange" />
              </span>
              {lesson.label}
            </span>
          }
        />
        <StepCard k={`learn-${learnIndex}`}>
          <Dots total={lessons.length} at={learnIndex} />
          <h2 className="font-heading text-xl font-bold text-ink sm:text-2xl">{lesson.line}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {(lesson.examples || []).map((ex: string) => {
              const good = ex.startsWith("✅")
              return (
                <p key={ex} className={cn("flex items-start gap-3 rounded-2xl px-4 py-3 text-base", good ? "bg-success-soft text-success" : "bg-danger-soft text-danger")}>
                  <Icon name={good ? "check-circle" : "x"} className="mt-0.5 size-5" />
                  <span className="text-ink">{ex.replace(/^[✅❌]\s*/u, "")}</span>
                </p>
              )
            })}
          </div>
          <Nav onBack={learnIndex > 0 ? () => setLearnIndex((i) => i - 1) : () => setPhase("intro")}>
            {learnIndex < lessons.length - 1 ? (
              <Button onClick={() => setLearnIndex((i) => i + 1)}>
                Tiếp <Icon name="arrow-right" className="size-4" />
              </Button>
            ) : (
              <Button onClick={startQuiz}>
                Làm {learning.quiz_size || questions.length} câu quiz <Icon name="arrow-right" className="size-4" />
              </Button>
            )}
          </Nav>
        </StepCard>
        <StatusNote tone="error">{message}</StatusNote>
      </>
    )
  }

  if (phase === "quiz" && questions[quizIndex]) {
    const q = questions[quizIndex]
    const last = quizIndex === questions.length - 1
    return (
      <>
        <PageHeader eyebrow="Quiz 4F" title={`Câu ${quizIndex + 1}/${questions.length}`} />
        <StepCard k={`quiz-${quizIndex}`}>
          <Meter value={((quizIndex + 1) / questions.length) * 100} />
          <h2 className="font-heading text-xl font-bold text-ink">{q.prompt}</h2>
          <div role="radiogroup" aria-label={q.prompt} className="flex flex-col gap-2.5">
            {(q.options || []).map((opt: string, i: number) => {
              const on = answers[q.id] === i
              return (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: i }))}
                  className={cn(
                    "flex items-center gap-3 rounded-2xl border-[1.5px] px-4 py-3 text-left text-base transition-colors",
                    on ? "border-orange bg-soft text-ink" : "border-edge bg-card text-ink hover:border-orange/60",
                  )}
                >
                  <span className={cn("grid size-6 shrink-0 place-items-center rounded-full border-[1.5px]", on ? "border-orange bg-orange text-white" : "border-edge")}>
                    {on && <Icon name="check" className="size-4" />}
                  </span>
                  {opt}
                </button>
              )
            })}
          </div>
          {last && (
            <label className="flex flex-col gap-2 rounded-2xl bg-sand px-4 py-3 text-sm text-ink">
              <span className="flex justify-between">
                Con chắc chắn đến đâu? <b className="text-orange-strong">{confidence}%</b>
              </span>
              <input type="range" min="0" max="100" step="10" value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} className="accent-orange" />
            </label>
          )}
          <Nav onBack={quizIndex > 0 ? () => setQuizIndex((i) => i - 1) : undefined}>
            {!last ? (
              <Button disabled={answers[q.id] == null} onClick={() => setQuizIndex((i) => i + 1)}>
                Tiếp <Icon name="arrow-right" className="size-4" />
              </Button>
            ) : (
              <Button disabled={busy || answers[q.id] == null} onClick={submitQuiz}>
                {busy ? "Đang chấm…" : "Xem kết quả"}
              </Button>
            )}
          </Nav>
          <StatusNote tone="error">{message}</StatusNote>
        </StepCard>
      </>
    )
  }

  if (phase === "result" && result) {
    const wrong = (result.feedback || []).filter((f: any) => !f.correct)
    const ready = !!result.ready_to_reflect
    return (
      <>
        <PageHeader eyebrow="Kết quả" title={ready ? "Con đã hiểu 4F!" : "Luyện thêm một chút"} />
        <StepCard k="result">
          <div className="flex flex-col items-center gap-3 text-center">
            <img src={`/brand/sutucon/mascots-2d/${ready ? "lion-celebrate" : "meerkat-thinking"}.svg`} alt="" className="size-28" />
            <strong className={cn("font-heading text-5xl font-bold", ready ? "text-success" : "text-orange")}>{score100(result.score)}%</strong>
            <p className="text-base text-muted-foreground">
              {ready ? "Con có thể bắt đầu viết Reflection." : `Simba đã chọn ${result.quiz_size} câu cần luyện lại.`}
            </p>
          </div>
          {wrong.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-ink">{wrong.length} điều cần nhớ</p>
              {wrong.slice(0, 4).map((f: any) => (
                <div key={f.question_id} className="flex flex-col gap-1 rounded-2xl bg-sand px-4 py-3 text-sm">
                  <b className="text-ink">{f.explanation}</b>
                  {f.example && <span className="text-muted-foreground">Ví dụ: {f.example}</span>}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-center">
            <Button size="lg" onClick={() => (ready ? onWrite() : startQuiz())}>
              {ready ? "Viết Reflection" : `Luyện ${result.quiz_size} câu`} <Icon name="arrow-right" className="size-4" />
            </Button>
          </div>
        </StepCard>
      </>
    )
  }
  return null
}
